<template>
  <main
    class="site-page relative min-h-screen overflow-hidden bg-[var(--page)] text-[var(--text)] antialiased"
    :style="{
      '--pointer-x': `${pointer.x}%`,
      '--pointer-y': `${pointer.y}%`
    }"
    @pointermove="handlePointerMove"
  >
    <!-- PROGRESSO -->
    <div
      class="fixed left-0 right-0 top-16 z-40 h-[2px] bg-transparent"
      aria-hidden="true"
    >
      <div
        class="reading-progress h-full origin-left bg-[var(--text-strong)]"
        :style="{ width: `${readingProgress}%` }"
      ></div>
    </div>

    <!-- AMBIENTE -->
    <div class="pointer-events-none fixed inset-0 z-0 overflow-hidden" aria-hidden="true">
      <div class="ambient-grid absolute inset-0"></div>
      <div class="ambient-noise absolute inset-0"></div>
      <div class="pointer-glow absolute inset-0"></div>
      <div class="ambient-orb ambient-orb-one"></div>
      <div class="ambient-orb ambient-orb-two"></div>
    </div>

    <!-- VOLTAR AO TOPO -->
    <transition name="scroll-top">
      <button
        v-show="showScrollTop"
        type="button"
        class="fixed bottom-5 right-5 z-50 grid h-11 w-11 place-items-center rounded-full border border-[var(--border)] bg-[var(--glass-strong)] text-[var(--text-strong)] shadow-[var(--shadow-soft)] backdrop-blur-2xl transition duration-300 hover:-translate-y-1 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[var(--focus)]"
        aria-label="Voltar ao topo"
        title="Voltar ao topo"
        @click="scrollToTop"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.8"
          class="h-5 w-5"
          aria-hidden="true"
        >
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 15l6-6 6 6" />
        </svg>
      </button>
    </transition>

    <article class="relative z-10">
      <div class="mx-auto max-w-7xl px-5 pb-24 pt-12 sm:px-6 lg:px-8 lg:pb-28 lg:pt-16">
        <router-link
          to="/blog"
          class="secondary-button inline-flex h-10 items-center gap-2 rounded-full px-4 text-xs font-semibold"
        >
          <span aria-hidden="true">←</span>
          Voltar ao blog
        </router-link>

        <!-- LOADING -->
        <div v-if="loading" class="mx-auto mt-12 max-w-6xl">
          <div class="mx-auto max-w-4xl text-center">
            <div class="mx-auto h-7 w-40 animate-pulse rounded-full bg-[var(--chip)]"></div>
            <div class="mx-auto mt-7 h-16 w-4/5 animate-pulse rounded-[1.5rem] bg-[var(--chip)]"></div>
            <div class="mx-auto mt-4 h-5 w-2/3 animate-pulse rounded-full bg-[var(--chip)]"></div>
          </div>

          <div class="mt-12 aspect-[16/9] animate-pulse rounded-[2.5rem] bg-[var(--card)] ring-1 ring-[var(--border)]"></div>
        </div>

        <!-- NOT FOUND -->
        <div
          v-else-if="!post"
          class="mx-auto mt-12 max-w-3xl rounded-[2.5rem] border border-[var(--border)] bg-[var(--glass)] p-8 text-center shadow-[var(--shadow-large)] backdrop-blur-2xl sm:p-10"
        >
          <div
            class="mx-auto grid h-12 w-12 place-items-center rounded-2xl bg-[var(--text-strong)] text-sm font-bold text-[var(--inverse)]"
          >
            ?
          </div>

          <p class="mt-6 text-xs font-semibold uppercase tracking-[0.16em] text-[var(--muted)]">
            Artigo indisponível
          </p>

          <h1
            class="mt-4 text-4xl font-semibold tracking-[-0.045em] text-[var(--text-strong)]"
          >
            Não encontramos esse post.
          </h1>

          <p class="mx-auto mt-4 max-w-xl text-base leading-7 text-[var(--muted)]">
            O artigo pode ter sido removido, alterado ou estar temporariamente indisponível.
          </p>

          <router-link
            to="/blog"
            class="primary-button mt-8 inline-flex h-12 items-center justify-center rounded-full px-7 text-sm font-semibold"
          >
            Ver blog
          </router-link>
        </div>

        <!-- POST -->
        <div v-else class="post-ready">
          <!-- HERO EDITORIAL -->
          <header class="mx-auto mt-12 max-w-5xl text-center">
            <div class="flex flex-wrap items-center justify-center gap-2">
              <span
                v-if="post.date"
                class="rounded-full border border-[var(--border)] bg-[var(--glass)] px-4 py-2 text-[11px] font-semibold text-[var(--muted)] backdrop-blur-xl"
              >
                {{ post.date }}
              </span>

              <span
                v-for="tag in post.tags || []"
                :key="tag"
                class="rounded-full border border-[var(--border)] bg-[var(--glass)] px-4 py-2 text-[11px] font-semibold text-[var(--muted)] backdrop-blur-xl"
              >
                {{ tag }}
              </span>

              <span
                v-if="post.source"
                class="rounded-full bg-[var(--text-strong)] px-4 py-2 text-[11px] font-semibold text-[var(--inverse)]"
              >
                {{ sourceLabel(post.source) }}
              </span>
            </div>

            <h1
              class="mx-auto mt-8 max-w-5xl text-5xl font-semibold tracking-[-0.06em] text-[var(--text-strong)] sm:text-6xl lg:text-[4.8rem] lg:leading-[1.01]"
            >
              {{ post.title }}
            </h1>

            <p
              v-if="post.excerpt"
              class="mx-auto mt-7 max-w-3xl text-lg leading-8 text-[var(--muted)] sm:text-xl"
            >
              {{ post.excerpt }}
            </p>

            <div class="mt-8 flex flex-wrap items-center justify-center gap-3">
              <span
                class="inline-flex items-center gap-2 rounded-full border border-[var(--border)] bg-[var(--glass)] px-4 py-2 text-xs font-semibold text-[var(--muted)] backdrop-blur-xl"
              >
                <span class="h-1.5 w-1.5 rounded-full bg-emerald-500"></span>
                {{ readingTime }} min de leitura
              </span>

              <a
                v-if="post.link"
                :href="post.link"
                target="_blank"
                rel="noopener"
                class="primary-button inline-flex h-11 items-center justify-center gap-2 rounded-full px-5 text-xs font-semibold"
              >
                Ler fonte original
                <span aria-hidden="true">↗</span>
              </a>
            </div>
          </header>

          <!-- CAPA -->
          <div class="relative mx-auto mt-12 max-w-6xl">
            <div class="hero-halo absolute inset-x-20 bottom-0 h-32 rounded-full blur-3xl"></div>

            <div
              class="cover-frame relative overflow-hidden rounded-[2.6rem] border border-[var(--border)] bg-[var(--glass)] p-2.5 shadow-[var(--shadow-large)] backdrop-blur-2xl sm:p-3"
            >
              <div
                v-if="post.image"
                class="aspect-[16/9] overflow-hidden rounded-[2rem] bg-[var(--chip)]"
              >
                <img
                  :src="post.image"
                  :alt="post.title"
                  class="h-full w-full object-cover"
                  loading="eager"
                />
              </div>

              <div
                v-else
                class="relative grid aspect-[16/9] place-items-center overflow-hidden rounded-[2rem] bg-[#090a0c] px-8 text-center text-white"
              >
                <div class="article-grid pointer-events-none absolute inset-0"></div>

                <div class="relative max-w-3xl">
                  <p class="text-xs font-semibold uppercase tracking-[0.28em] text-zinc-500">
                    {{ sourceLabel(post.source) }}
                  </p>

                  <p
                    class="mt-5 text-4xl font-semibold tracking-[-0.045em] sm:text-5xl lg:text-6xl"
                  >
                    {{ post.title }}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- CONECTOR -->
          <div class="relative mx-auto h-20 max-w-6xl" aria-hidden="true">
            <span class="flow-line"></span>
            <span class="flow-node"></span>
          </div>

          <!-- LEITURA -->
          <div
            class="mx-auto grid max-w-6xl gap-6 lg:grid-cols-[14.5rem_minmax(0,1fr)] lg:gap-8"
          >
            <aside class="hidden lg:block">
              <div
                class="sticky top-28 space-y-4"
              >
                <div
                  class="rounded-[1.7rem] border border-[var(--border)] bg-[var(--glass)] p-5 shadow-[var(--shadow-soft)] backdrop-blur-2xl"
                >
                  <p
                    class="text-[10px] font-semibold uppercase tracking-[0.18em] text-[var(--muted-2)]"
                  >
                    Leitura
                  </p>

                  <div class="mt-5 space-y-4">
                    <div>
                      <p class="text-[10px] uppercase tracking-[0.12em] text-[var(--muted-2)]">
                        Fonte
                      </p>
                      <p class="mt-1 text-sm font-semibold text-[var(--text-strong)]">
                        {{ sourceLabel(post.source) }}
                      </p>
                    </div>

                    <div v-if="post.date">
                      <p class="text-[10px] uppercase tracking-[0.12em] text-[var(--muted-2)]">
                        Publicado
                      </p>
                      <p class="mt-1 text-sm font-semibold text-[var(--text-strong)]">
                        {{ post.date }}
                      </p>
                    </div>

                    <div>
                      <p class="text-[10px] uppercase tracking-[0.12em] text-[var(--muted-2)]">
                        Tempo
                      </p>
                      <p class="mt-1 text-sm font-semibold text-[var(--text-strong)]">
                        {{ readingTime }} min
                      </p>
                    </div>
                  </div>

                  <div class="mt-6 border-t border-[var(--border)] pt-5">
                    <div class="mb-2 flex items-center justify-between text-[10px] font-semibold text-[var(--muted)]">
                      <span>Progresso</span>
                      <span>{{ Math.round(readingProgress) }}%</span>
                    </div>

                    <div class="h-1.5 overflow-hidden rounded-full bg-[var(--chip)]">
                      <div
                        class="h-full rounded-full bg-[var(--text-strong)] transition-[width] duration-150"
                        :style="{ width: `${readingProgress}%` }"
                      ></div>
                    </div>
                  </div>
                </div>

                <router-link
                  to="/blog"
                  class="secondary-button inline-flex h-11 w-full items-center justify-center rounded-full px-5 text-xs font-semibold"
                >
                  Mais artigos
                </router-link>
              </div>
            </aside>

            <section
              class="article-shell min-w-0 rounded-[2.5rem] border border-[var(--border)] bg-[var(--card)] p-6 shadow-[var(--shadow-soft)] backdrop-blur-xl sm:p-8 lg:p-10 xl:p-12"
            >
              <div
                v-if="compiled"
                class="article-content"
                v-html="compiled"
              ></div>

              <div
                v-else
                class="rounded-[2rem] border border-[var(--border)] bg-[var(--surface)] p-8 text-center"
              >
                <p class="text-lg font-semibold text-[var(--text-strong)]">
                  Conteúdo disponível na fonte original.
                </p>

                <p class="mt-2 text-sm leading-6 text-[var(--muted)]">
                  Algumas notícias externas não liberam o artigo completo pelo feed.
                </p>

                <a
                  v-if="post.link"
                  :href="post.link"
                  target="_blank"
                  rel="noopener"
                  class="primary-button mt-6 inline-flex h-12 items-center justify-center gap-2 rounded-full px-7 text-sm font-semibold"
                >
                  Abrir notícia
                  <span aria-hidden="true">↗</span>
                </a>
              </div>
            </section>
          </div>

          <!-- CTA EDITORIAL -->
          <div
            class="mx-auto mt-20 max-w-6xl rounded-[2.7rem] bg-[#090a0c] p-7 text-white shadow-[var(--shadow-large)] sm:p-10 lg:p-12"
          >
            <div class="grid gap-8 lg:grid-cols-12 lg:items-center">
              <div class="lg:col-span-8">
                <p class="text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500">
                  Continue explorando
                </p>

                <h2 class="mt-4 text-3xl font-semibold tracking-[-0.045em] sm:text-4xl">
                  Conteúdo organiza ideias.
                  <span class="text-zinc-500">Produto transforma em operação.</span>
                </h2>
              </div>

              <div class="flex flex-col gap-3 lg:col-span-4">
                <router-link
                  to="/blog"
                  class="inline-flex h-12 items-center justify-center rounded-full bg-white px-6 text-sm font-semibold text-black transition hover:-translate-y-0.5 hover:bg-zinc-200"
                >
                  Ver mais artigos
                </router-link>

                <router-link
                  to="/solutions"
                  class="inline-flex h-12 items-center justify-center rounded-full bg-white/[0.07] px-6 text-sm font-semibold text-white ring-1 ring-white/10 transition hover:bg-white/[0.11]"
                >
                  Ver soluções
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </article>
  </main>
</template>

<script setup>
import { computed, onMounted, onUnmounted, reactive, ref } from 'vue'
import { useRoute } from 'vue-router'
import { posts as localPosts } from '@/data/blog'

const route = useRoute()
const slug = String(route.params.slug || '')
const post = ref(null)
const loading = ref(true)
const showScrollTop = ref(false)
const readingProgress = ref(0)
const pointer = reactive({ x: 50, y: 20 })

function sourceLabel(value) {
  if (value === 'devto') return 'DEV.to'
  if (value === 'hn') return 'Hacker News'
  if (value === 'local') return 'Blog'
  return value || 'Blog'
}

function escapeHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')
}

function formatInline(value) {
  return String(value || '')
    .replace(/!\[([^\]]*)\]\((https?:\/\/[^\s)]+)\)/gim, '<img src="$2" alt="$1" loading="lazy" />')
    .replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/gim, '<a href="$2" target="_blank" rel="noopener">$1</a>')
    .replace(/\*\*([^*]+)\*\*/gim, '<strong>$1</strong>')
    .replace(/\*([^*]+)\*/gim, '<em>$1</em>')
    .replace(/`([^`]+)`/gim, '<code>$1</code>')
}

function mdToHtml(markdown) {
  const lines = String(markdown || '').split('\n')
  const html = []
  let paragraph = []
  let unorderedList = []
  let orderedList = []
  let quote = []
  let code = []
  let codeLang = ''
  let inCode = false

  function flushParagraph() {
    if (paragraph.length) {
      html.push(`<p>${formatInline(escapeHtml(paragraph.join(' ')))}</p>`)
      paragraph = []
    }
  }

  function flushUnorderedList() {
    if (unorderedList.length) {
      html.push(`<ul>${unorderedList.map(item => `<li>${formatInline(escapeHtml(item))}</li>`).join('')}</ul>`)
      unorderedList = []
    }
  }

  function flushOrderedList() {
    if (orderedList.length) {
      html.push(`<ol>${orderedList.map(item => `<li>${formatInline(escapeHtml(item))}</li>`).join('')}</ol>`)
      orderedList = []
    }
  }

  function flushQuote() {
    if (quote.length) {
      html.push(`<blockquote>${quote.map(item => `<p>${formatInline(escapeHtml(item))}</p>`).join('')}</blockquote>`)
      quote = []
    }
  }

  function flushAll() {
    flushParagraph()
    flushUnorderedList()
    flushOrderedList()
    flushQuote()
  }

  function flushCode() {
    html.push(`<pre data-lang="${escapeHtml(codeLang || 'code')}"><code>${escapeHtml(code.join('\n'))}</code></pre>`)
    code = []
    codeLang = ''
  }

  for (const line of lines) {
    const raw = line
    const trimmed = raw.trim()

    if (trimmed.startsWith('```')) {
      if (inCode) {
        flushCode()
        inCode = false
      } else {
        flushAll()
        inCode = true
        codeLang = trimmed.replace('```', '').trim()
      }
      continue
    }

    if (inCode) {
      code.push(raw)
      continue
    }

    if (!trimmed) {
      flushAll()
      continue
    }

    if (trimmed.startsWith('### ')) {
      flushAll()
      html.push(`<h3>${formatInline(escapeHtml(trimmed.replace(/^### /, '')))}</h3>`)
      continue
    }

    if (trimmed.startsWith('## ')) {
      flushAll()
      html.push(`<h2>${formatInline(escapeHtml(trimmed.replace(/^## /, '')))}</h2>`)
      continue
    }

    if (trimmed.startsWith('# ')) {
      flushAll()
      html.push(`<h1>${formatInline(escapeHtml(trimmed.replace(/^# /, '')))}</h1>`)
      continue
    }

    if (trimmed.startsWith('> ')) {
      flushParagraph()
      flushUnorderedList()
      flushOrderedList()
      quote.push(trimmed.replace(/^> /, ''))
      continue
    }

    if (/^[-*]\s+/.test(trimmed)) {
      flushParagraph()
      flushOrderedList()
      flushQuote()
      unorderedList.push(trimmed.replace(/^[-*]\s+/, ''))
      continue
    }

    if (/^\d+\.\s+/.test(trimmed)) {
      flushParagraph()
      flushUnorderedList()
      flushQuote()
      orderedList.push(trimmed.replace(/^\d+\.\s+/, ''))
      continue
    }

    flushUnorderedList()
    flushOrderedList()
    flushQuote()
    paragraph.push(trimmed)
  }

  if (inCode) {
    flushCode()
  }

  flushAll()

  return html.join('')
}

const compiled = computed(() => {
  if (!post.value) return ''
  if (post.value.contentHtml) return post.value.contentHtml
  return mdToHtml(post.value.content || '')
})

const readingTime = computed(() => {
  const text = String(post.value?.content || post.value?.excerpt || post.value?.title || '')
  const words = text.trim().split(/\s+/).filter(Boolean).length
  return Math.max(1, Math.ceil(words / 220))
})

async function loadPost() {
  loading.value = true

  const statePost = history.state && history.state.post

  if (statePost && statePost.slug === slug) {
    post.value = normalizePost(statePost)
    loading.value = false
    return
  }

  const local = localPosts.find(item => item.slug === slug)

  if (local) {
    post.value = normalizePost({
      slug: local.slug,
      title: local.title,
      excerpt: local.excerpt || '',
      date: local.date,
      tags: local.tags || [],
      image: local.image || null,
      link: local.link || null,
      source: 'local',
      content: local.content || ''
    })

    loading.value = false
    return
  }

  if (slug.startsWith('devto-')) {
    const id = slug.replace('devto-', '')

    try {
      const response = await fetch(`https://dev.to/api/articles/${id}`)

      if (response.ok) {
        const article = await response.json()

        post.value = normalizePost({
          slug,
          title: article.title,
          excerpt: article.description || '',
          date: (article.published_at || '').slice(0, 10),
          tags: (article.tag_list || []).slice(0, 4),
          image: article.cover_image || article.social_image || null,
          link: article.url,
          source: 'devto',
          content: article.body_markdown || ''
        })
      }
    } catch {
      post.value = null
    }

    loading.value = false
    return
  }

  if (slug.startsWith('hn-')) {
    const id = slug.replace('hn-', '')

    try {
      const response = await fetch(`https://hn.algolia.com/api/v1/items/${id}`)

      if (response.ok) {
        const item = await response.json()

        post.value = normalizePost({
          slug,
          title: item.title || '(sem título)',
          excerpt: item.url || '',
          date: (item.created_at || '').slice(0, 10),
          tags: ['Hacker News'],
          image: null,
          link: item.url || `https://news.ycombinator.com/item?id=${id}`,
          source: 'hn',
          content: item.text ? stripHtml(item.text) : ''
        })
      }
    } catch {
      post.value = null
    }

    loading.value = false
    return
  }

  post.value = null
  loading.value = false
}

function normalizePost(item) {
  return {
    slug: item.slug || '',
    title: item.title || 'Sem título',
    excerpt: item.excerpt || item.description || '',
    date: item.date || '',
    tags: item.tags || [],
    image: item.image || null,
    link: item.link || null,
    source: item.source || 'local',
    content: item.content || '',
    contentHtml: item.contentHtml || ''
  }
}

function stripHtml(value) {
  return String(value || '')
    .replace(/<p>/g, '')
    .replace(/<\/p>/g, '\n\n')
    .replace(/<br\s*\/?>/g, '\n')
    .replace(/<[^>]+>/g, '')
}



function handlePointerMove(event) {
  const width = window.innerWidth || 1
  const height = window.innerHeight || 1

  pointer.x = Math.max(0, Math.min(100, (event.clientX / width) * 100))
  pointer.y = Math.max(0, Math.min(100, (event.clientY / height) * 100))
}

function handleScroll() {
  showScrollTop.value = window.scrollY > 520

  const scrollable =
    document.documentElement.scrollHeight - window.innerHeight

  readingProgress.value =
    scrollable > 0
      ? Math.min(100, Math.max(0, (window.scrollY / scrollable) * 100))
      : 0
}

function scrollToTop() {
  window.scrollTo({
    top: 0,
    behavior: 'smooth'
  })
}


onMounted(() => {
  loadPost()
  handleScroll()

  window.addEventListener('scroll', handleScroll, {
    passive: true
  })
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
})
</script>

<style scoped>
:global(:root) {
  --page: #f5f5f7;
  --surface: rgba(255, 255, 255, 0.92);
  --card: rgba(255, 255, 255, 0.80);
  --chip: rgba(235, 235, 239, 0.88);
  --glass: rgba(255, 255, 255, 0.56);
  --glass-strong: rgba(255, 255, 255, 0.86);
  --text: #27272a;
  --text-strong: #09090b;
  --muted: #66666d;
  --muted-2: #a1a1aa;
  --inverse: #ffffff;
  --border: rgba(24, 24, 27, 0.085);
  --focus: rgba(113, 113, 122, 0.18);
  --shadow-soft: 0 12px 38px rgba(24, 24, 27, 0.07);
  --shadow-large: 0 32px 100px rgba(24, 24, 27, 0.105);
  --orb-one: rgba(147, 161, 185, 0.22);
  --orb-two: rgba(203, 213, 225, 0.31);
}

:global(html[data-theme='dark']) {
  --page: #08090b;
  --surface: rgba(15, 16, 19, 0.92);
  --card: rgba(19, 20, 24, 0.80);
  --chip: rgba(35, 36, 41, 0.92);
  --glass: rgba(19, 20, 23, 0.56);
  --glass-strong: rgba(18, 19, 22, 0.86);
  --text: #e4e4e7;
  --text-strong: #fafafa;
  --muted: #a1a1aa;
  --muted-2: #5f6169;
  --inverse: #09090b;
  --border: rgba(255, 255, 255, 0.085);
  --focus: rgba(212, 212, 216, 0.13);
  --shadow-soft: 0 12px 42px rgba(0, 0, 0, 0.30);
  --shadow-large: 0 36px 120px rgba(0, 0, 0, 0.50);
  --orb-one: rgba(71, 85, 105, 0.23);
  --orb-two: rgba(63, 63, 70, 0.28);
}

:global(html) {
  background: var(--page);
  transition: background-color 350ms ease;
}

.site-page {
  transition:
    background-color 350ms ease,
    color 350ms ease;
}

.reading-progress {
  transition:
    width 100ms linear,
    background-color 350ms ease;
}

.ambient-grid {
  opacity: 0.30;
  background-image:
    linear-gradient(to right, var(--border) 1px, transparent 1px),
    linear-gradient(to bottom, var(--border) 1px, transparent 1px);
  background-size: 64px 64px;
  mask-image: linear-gradient(to bottom, black, transparent 86%);
}

.ambient-noise {
  opacity: 0.021;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 180 180' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.85' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='.85'/%3E%3C/svg%3E");
}

.pointer-glow {
  background:
    radial-gradient(
      42rem circle at var(--pointer-x) var(--pointer-y),
      var(--orb-one),
      transparent 68%
    );
}

.ambient-orb {
  position: absolute;
  border-radius: 9999px;
  filter: blur(95px);
}

.ambient-orb-one {
  left: -11rem;
  top: 8rem;
  width: 34rem;
  height: 34rem;
  background: var(--orb-one);
  animation: float-a 14s ease-in-out infinite;
}

.ambient-orb-two {
  right: -12rem;
  top: 55rem;
  width: 38rem;
  height: 38rem;
  background: var(--orb-two);
  animation: float-b 18s ease-in-out infinite;
}

.hero-halo {
  background: var(--orb-one);
}

.primary-button {
  color: var(--inverse);
  background: var(--text-strong);
  box-shadow:
    0 8px 24px rgba(0, 0, 0, 0.10),
    inset 0 1px 0 rgba(255, 255, 255, 0.12);
  transition:
    transform 300ms ease,
    box-shadow 300ms ease;
}

.primary-button:hover {
  transform: translateY(-2px);
  box-shadow:
    0 15px 32px rgba(0, 0, 0, 0.16),
    inset 0 1px 0 rgba(255, 255, 255, 0.13);
}

.secondary-button {
  color: var(--text-strong);
  background: var(--glass-strong);
  border: 1px solid var(--border);
  box-shadow: var(--shadow-soft);
  backdrop-filter: blur(18px);
  transition:
    transform 300ms ease,
    border-color 300ms ease;
}

.secondary-button:hover {
  transform: translateY(-2px);
  border-color: color-mix(in srgb, var(--text-strong) 18%, transparent);
}

.cover-frame {
  transition:
    transform 450ms ease,
    box-shadow 450ms ease;
}

.cover-frame:hover {
  transform: translateY(-2px);
}

.article-grid {
  opacity: 0.30;
  background-image:
    linear-gradient(to right, rgba(255, 255, 255, 0.055) 1px, transparent 1px),
    linear-gradient(to bottom, rgba(255, 255, 255, 0.055) 1px, transparent 1px);
  background-size: 48px 48px;
  mask-image: radial-gradient(circle at center, black, transparent 75%);
}

.flow-line {
  position: absolute;
  left: 50%;
  top: 0;
  width: 1px;
  height: 100%;
  background:
    linear-gradient(
      to bottom,
      transparent,
      var(--border) 24%,
      var(--border) 76%,
      transparent
    );
}

.flow-node {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 9px;
  height: 9px;
  border: 2px solid var(--page);
  border-radius: 9999px;
  background: var(--text-strong);
  box-shadow:
    0 0 0 5px color-mix(in srgb, var(--text-strong) 8%, transparent);
  transform: translate(-50%, -50%);
}

.post-ready {
  animation: post-in 650ms cubic-bezier(.22, .82, .22, 1) both;
}

.article-content {
  color: var(--text);
  font-size: 1rem;
  line-height: 1.9;
}

.article-content :deep(h1),
.article-content :deep(h2),
.article-content :deep(h3) {
  color: var(--text-strong);
  font-weight: 680;
  letter-spacing: -0.04em;
  line-height: 1.08;
}

.article-content :deep(h1) {
  margin-top: 2.8rem;
  font-size: clamp(2.25rem, 5vw, 4rem);
}

.article-content :deep(h2) {
  margin-top: 2.8rem;
  font-size: clamp(1.85rem, 3vw, 2.75rem);
}

.article-content :deep(h3) {
  margin-top: 2.2rem;
  font-size: clamp(1.35rem, 2vw, 1.75rem);
}

.article-content :deep(p) {
  margin-top: 1.2rem;
}

.article-content :deep(a) {
  color: var(--text-strong);
  font-weight: 650;
  text-decoration: underline;
  text-decoration-color: color-mix(in srgb, var(--text-strong) 35%, transparent);
  text-underline-offset: 4px;
}

.article-content :deep(strong) {
  color: var(--text-strong);
  font-weight: 700;
}

.article-content :deep(ul),
.article-content :deep(ol) {
  margin-top: 1.25rem;
  padding-left: 1.4rem;
}

.article-content :deep(ul) {
  list-style: disc;
}

.article-content :deep(ol) {
  list-style: decimal;
}

.article-content :deep(li) {
  margin-top: 0.65rem;
  padding-left: 0.25rem;
}

.article-content :deep(code) {
  border-radius: 0.55rem;
  background: var(--chip);
  padding: 0.17rem 0.42rem;
  color: var(--text-strong);
  font-size: 0.9em;
}

.article-content :deep(pre) {
  position: relative;
  margin-top: 1.9rem;
  overflow-x: auto;
  border-radius: 1.5rem;
  background: #09090b;
  padding: 3rem 1.25rem 1.25rem;
  color: #e4e4e7;
  box-shadow:
    inset 0 0 0 1px rgba(255, 255, 255, 0.08),
    0 20px 50px rgba(0, 0, 0, 0.12);
}

.article-content :deep(pre)::before {
  content: attr(data-lang);
  position: absolute;
  left: 1.25rem;
  top: 1rem;
  color: #71717a;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.article-content :deep(pre code) {
  background: transparent;
  padding: 0;
  color: inherit;
  font-size: 0.9rem;
  line-height: 1.75;
  white-space: pre;
}

.article-content :deep(blockquote) {
  margin-top: 1.9rem;
  border-left: 3px solid var(--text-strong);
  border-radius: 0 1.25rem 1.25rem 0;
  background: var(--surface);
  padding: 1rem 1.25rem;
  color: var(--muted);
}

.article-content :deep(img) {
  margin-top: 2rem;
  overflow: hidden;
  border-radius: 1.5rem;
  width: 100%;
  border: 1px solid var(--border);
  box-shadow: var(--shadow-soft);
}

.scroll-top-enter-active,
.scroll-top-leave-active {
  transition:
    opacity 0.25s ease,
    transform 0.25s ease;
}

.scroll-top-enter-from,
.scroll-top-leave-to {
  opacity: 0;
  transform: translateY(12px) scale(0.92);
}

@keyframes post-in {
  from {
    opacity: 0;
    transform: translateY(18px) scale(.995);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes float-a {
  0%, 100% { transform: translate3d(0, 0, 0); }
  50% { transform: translate3d(3rem, 2rem, 0); }
}

@keyframes float-b {
  0%, 100% { transform: translate3d(0, 0, 0); }
  50% { transform: translate3d(-3rem, -2rem, 0); }
}

@media (max-width: 640px) {
  .ambient-grid {
    background-size: 44px 44px;
  }
}

@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    scroll-behavior: auto !important;
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}
</style>